import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import Chevron from "./Chevron";

/**
 * The avatar at the foot of the drawer, which opens the screens that are set
 * up once and audited now and then — shop profile, staff, devices, journal,
 * settings — and signing out. They live here so the drawer holds only what
 * gets opened every day.
 */
const AccountMenu = ({ user, role, items, isActive, onNavigate, onLogout }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const away = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    const escape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", away);
    document.addEventListener("touchstart", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("touchstart", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      {open && (
        <div
          role="menu"
          className="absolute bottom-full inset-x-0 mb-2 rounded-lg bg-white shadow-xl py-1 max-h-[70vh] overflow-y-auto"
        >
          {items.map((item) => (
            <Link
              key={item.key}
              to={item.path}
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onNavigate();
              }}
              className={`flex items-center px-4 py-3 text-sm ${
                isActive(item.path)
                  ? "bg-primary-50 text-primary-700 font-semibold"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span className="text-xl me-3">{item.icon}</span>
              {t(`nav.${item.key}`)}
            </Link>
          ))}
          <button
            type="button"
            role="menuitem"
            onClick={onLogout}
            className={`w-full flex items-center px-4 py-3 text-sm text-red-600 hover:bg-red-50 text-start ${
              items.length ? "border-t border-gray-100" : ""
            }`}
          >
            <span className="text-xl me-3">🚪</span>
            {t("nav.logout")}
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("nav.accountMenu")}
        className="w-full flex items-center justify-between gap-3 rounded-lg text-start hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-white p-1 -m-1"
      >
        <span className="flex items-center gap-3 min-w-0">
          <span className="shrink-0 w-10 h-10 rounded-full bg-white flex items-center justify-center text-primary-600 font-bold">
            {user?.name?.charAt(0).toUpperCase()}
          </span>
          <span className="text-white min-w-0">
            <span className="block font-medium text-sm truncate">
              {user?.name}
            </span>
            {role && (
              <span className="block text-xs text-primary-200">
                {t(`nav.roles.${role}`)}
              </span>
            )}
          </span>
        </span>
        <Chevron direction={open ? "down" : "up"} className="text-primary-200" />
      </button>
    </div>
  );
};

export default AccountMenu;
